import * as Popover from "@radix-ui/react-popover";
import { Check, ChevronDown, Globe } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import type { DistrictRow, Scope } from "@busright/data-contract";

import { useScope } from "@/context/ScopeContext";
import { useDistricts } from "@/hooks/queries";
import { FLEET_SCOPE, scopeDisplayName, scopeEquals } from "@/lib/scope";

// =================================================================================================
// ScopeSwitcher — the shell-level scope / DISTRICT switcher. Sets the global `Scope` (fleet by
// default; pick a district to re-scope). Per the contract, the district switcher is just "setting
// scope" — every scoped surface reads `useScope()` and re-fetches at the chosen grain.
//
// Districts come from /api/districts (human account name + district_id). Names lead; ids are
// secondary. A type-ahead filter narrows the list (the per-page scoped-search direction from the
// feedback). Built on a Radix Popover so it's keyboard-accessible and closes on Escape/outside.
// =================================================================================================

// Build a district-level Scope from a DistrictRow (human label first, id secondary, fleet parent).
const districtScope = (row: DistrictRow): Scope => ({
  level: "district",
  id: row.districtId ?? undefined,
  label: row.accountName,
  parent: FLEET_SCOPE,
});

export const ScopeSwitcher = (): ReactNode => {
  const { scope, setScope } = useScope();
  const { data, isLoading } = useDistricts();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const rows = data?.data.rows ?? [];

  // Only districts with a resolved id are switchable targets; sort by human name.
  const options = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows
      .filter((r) => r.districtId !== null)
      .filter((r) => (q ? r.accountName.toLowerCase().includes(q) : true))
      .sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [rows, filter]);

  const pick = (next: Scope): void => {
    setScope(next);
    setOpen(false);
    setFilter("");
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="scope-switcher-trigger" aria-label="Change scope">
          <Globe size={14} aria-hidden />
          <span className="scope-switcher-label">{scopeDisplayName(scope)}</span>
          <ChevronDown size={14} aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="scope-switcher-menu" sideOffset={6} align="start">
          <input
            type="text"
            className="scope-switcher-filter"
            placeholder="Filter districts…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            // biome-ignore lint/a11y/noAutofocus: focus the filter when the scope menu opens
            autoFocus
          />
          <div className="scope-switcher-list" role="listbox" aria-label="Scope options">
            <button
              type="button"
              role="option"
              aria-selected={scopeEquals(scope, FLEET_SCOPE)}
              className="scope-switcher-option"
              onClick={() => pick(FLEET_SCOPE)}
            >
              <span>Entire fleet</span>
              {scopeEquals(scope, FLEET_SCOPE) ? <Check size={13} aria-hidden /> : null}
            </button>
            {isLoading ? (
              <div className="scope-switcher-empty">Loading districts…</div>
            ) : options.length === 0 ? (
              <div className="scope-switcher-empty">No matching districts</div>
            ) : (
              options.map((row) => {
                const next = districtScope(row);
                const selected = scopeEquals(scope, next);
                return (
                  <button
                    key={row.districtId}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className="scope-switcher-option"
                    onClick={() => pick(next)}
                  >
                    <span className="scope-switcher-option-name">
                      {row.accountName}
                      {row.state ? (
                        <span className="scope-switcher-option-sub">{row.state}</span>
                      ) : null}
                    </span>
                    {selected ? <Check size={13} aria-hidden /> : null}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
